import * as React from 'react';

import { DocumentType } from '@rematter/frontend-lib-api-client';
import {
  bulkDownloadFromUrl,
  Button,
  ButtonProps,
  Checkbox,
  ensureValue,
  Popover,
  printPdf,
  ThemeColor,
  useToast,
} from '@rematter/ui-web';
import { LucideChevronDown, LucideDownload, LucidePrinter } from 'lucide-react';
import { useLocalStorage } from 'usehooks-ts';

import { useGenerateDocuments } from '../../_graphql/mutationGenerateDocument';

import { useDebouncedEffect } from '~/shared/hooks/useDebounce';

export type DocumentOption = {
  label: string;
  documentType: DocumentType;
};

const getDocumentInput = (documentType: DocumentType, id: string) => {
  switch (documentType) {
    case 'BillOfLading':
      return {
        documentType,
        loadId: id,
      };

    case 'PackingList':
    case 'WeightTicket':
    case 'OutboundScaleTicket':
    case 'ThermalWeightTicket':
    case 'FullPageWeightTicket':
    case 'ThermalScaleTicket':
    case 'FullPageScaleTicket':
      return {
        documentType,
        loadId: id,
      };

    case 'SalesOrder':
      return {
        documentType,
        salesOrderId: id,
      };

    case 'PurchaseOrder':
      // Customer portal does not support PurchaseOrder documents
      // Customers see Sales Orders as their "purchase orders"
      throw new Error(
        'PurchaseOrder documents are not supported in customer portal',
      );

    case 'ShippingSummary':
      return {
        documentType,
        salesOrderId: id,
      };

    case 'BookingInvoice':
      return {
        documentType,
        bookingId: id,
      };

    case 'LoadInvoice':
      return {
        documentType,
        loadId: id,
        invoiceNumber: id,
      };

    case 'Invoice':
    default:
      return {
        documentType,
        invoiceNumber: id,
      };
  }
};

type FetchDocumentsPopoverProps = {
  type: 'print' | 'download';
  documents: DocumentOption[];
  /**
   * The key used to store the selected documents in local storage.
   * This is used to persist the selected documents between sessions.
   */
  originKey: string;
  /**
   * The ids of the documents to fetch.
   * This can be a function that returns a promise that resolves to an array of ids.
   * This is used to fetch the documents for the given ids.
   */
  ids: string[] | (() => string[]) | (() => Promise<string[]>);
  /**
   * The ref to the trigger button.
   * This is used to open the popover.
   */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  compressedFileName?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export const FetchDocumentsPopover = ({
  type,
  documents,
  originKey,
  ids,
  triggerRef,
  isOpen,
  onOpenChange,
  onSuccess,
  compressedFileName,
}: FetchDocumentsPopoverProps) => {
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [selectedDocuments, setSelectedDocuments] = useLocalStorage<
    DocumentType[]
  >(`${originKey}-${type}-documents`, []);

  const [focusedIndex, setFocusedIndex] = React.useState(0);
  const [generateDocuments, { isPending }] = useGenerateDocuments();
  const toast = useToast();

  const handleFetch = React.useCallback(async () => {
    const effectiveIds = typeof ids === 'function' ? await ids() : ids;

    const docsToFetch =
      documents.length === 1
        ? effectiveIds.map((id) =>
            getDocumentInput(documents[0].documentType, id),
          )
        : effectiveIds.flatMap((id) =>
            selectedDocuments.map((documentType) =>
              getDocumentInput(documentType, id),
            ),
          );

    if (!docsToFetch.length) {
      return;
    }

    try {
      const documentUrls = await generateDocuments(docsToFetch);

      if (type === 'print') {
        documentUrls
          .filter((it) => it.url)
          .forEach((it) => printPdf(ensureValue(it.url)));
      } else {
        await bulkDownloadFromUrl({
          files: documentUrls
            .filter((it) => it.url)
            .map((it) => ({
              url: ensureValue(it.url),
              fileName: `${it.documentType}-${it.fileId}.pdf`,
            })),
          fileName: `${compressedFileName}-${Date.now()}`,
        });
      }

      onOpenChange(false);
      onSuccess?.();
    } catch {
      if (documents.length === 1) {
        onOpenChange(false);
      }
      toast.error('Failed to fetch documents');
    }
  }, [
    ids,
    documents,
    generateDocuments,
    type,
    onOpenChange,
    onSuccess,
    selectedDocuments,
    compressedFileName,
    toast,
  ]);

  const handleCheckboxKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowDown') {
        setFocusedIndex((prev) => Math.min(prev + 1, documents.length - 1));
      } else if (event.key === 'ArrowUp') {
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
      } else if (event.key === 'Enter') {
        handleFetch();
      }
    },
    [documents.length, handleFetch],
  );

  const handleSelectionChange = React.useCallback(
    (documentType: DocumentType) => (checked: boolean) => {
      setSelectedDocuments((prev: DocumentType[]) =>
        checked
          ? [...prev, documentType]
          : prev.filter((it: DocumentType) => it !== documentType),
      );
    },
    [setSelectedDocuments],
  );

  React.useEffect(() => {
    const checkboxes = Array.from(
      popoverRef.current?.querySelectorAll('input[type="checkbox"]') ?? [],
    );
    if (checkboxes[focusedIndex] instanceof HTMLInputElement) {
      checkboxes[focusedIndex].focus();
    }
  }, [focusedIndex]);

  const handleFetchRef = React.useRef(handleFetch);
  handleFetchRef.current = handleFetch;

  useDebouncedEffect(
    () => isOpen && documents.length === 1 && handleFetchRef.current(),
    50,
    [isOpen, documents.length],
  );

  if (documents.length === 1) {
    return null;
  }

  return (
    <Popover
      triggerRef={triggerRef}
      placement="bottom right"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@FetchDocumentsPopover"
    >
      <div className="flex flex-col p-3 text-sm" ref={popoverRef}>
        <div className="mb-1.5 text-sm text-neutral/60">
          Select documents to {type === 'print' ? 'print' : 'download'}
        </div>
        {documents.map((document, index) => (
          <Checkbox
            key={document.label}
            autoFocus={index === 0}
            className={'py-1'}
            onKeyDown={handleCheckboxKeyDown}
            isSelected={selectedDocuments.includes(document.documentType)}
            isDisabled={isPending}
            onChange={handleSelectionChange(document.documentType)}
          >
            {document.label}
          </Checkbox>
        ))}
        <div className="mt-2 flex justify-end border-t border-neutral/10 pt-3">
          <Button
            color="primary"
            onClick={handleFetch}
            isLoading={isPending}
            isDisabled={!selectedDocuments.length}
          >
            {type === 'print' ? 'Print' : 'Download'}
          </Button>
        </div>
      </div>
    </Popover>
  );
};

type FetchDocumentsButtonProps = {
  originKey: string;
  showLabel?: boolean;
  color?: ThemeColor;
  variant?: ButtonProps['variant'];
  ids: string[] | (() => string[]) | (() => Promise<string[]>);
  documents: DocumentOption[];
  isDisabled?: boolean;
} & (
  | {
      type: 'print';
      compressedFileName?: never;
    }
  | {
      type: 'download';
      compressedFileName: string;
    }
);

const FetchDocumentsButton = ({
  type,
  showLabel = true,
  color,
  variant = 'outline',
  ids,
  documents,
  originKey,
  isDisabled,
  compressedFileName,
}: FetchDocumentsButtonProps) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="@FetchDocumentsButton contents">
      <Button
        ref={buttonRef}
        variant={variant}
        icon={type === 'print' ? <LucidePrinter /> : <LucideDownload />}
        color={isDisabled ? 'neutral' : color}
        onClick={() => setIsOpen((x) => !x)}
        className="flex items-center gap-1"
        isDisabled={isDisabled}
      >
        {showLabel && (type === 'print' ? 'Print' : 'Download')}
        {documents.length > 1 && (
          <LucideChevronDown size={'1em'} className="opacity-70" />
        )}
      </Button>
      {documents.length > 1 && (
        <FetchDocumentsPopover
          triggerRef={buttonRef}
          type={type}
          documents={documents}
          originKey={originKey}
          compressedFileName={compressedFileName}
          ids={ids}
          isOpen={isOpen}
          onOpenChange={setIsOpen}
        />
      )}
    </div>
  );
};

// eslint-disable-next-line custom/require-component-classname
export const DownloadDocumentsButton = ({
  compressedFileName: fileName,
  ...props
}: Omit<FetchDocumentsButtonProps, 'type'> & {
  compressedFileName: string;
}) => (
  <FetchDocumentsButton
    type="download"
    {...props}
    compressedFileName={fileName}
  />
);

// eslint-disable-next-line custom/require-component-classname
export const PrintDocumentsButton = (
  props: Omit<FetchDocumentsButtonProps, 'type' | 'compressedFileName'>,
) => <FetchDocumentsButton type="print" {...props} />;
